import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useId, useEffect } from 'react';
import { CheckMiniIcon } from '@iris/react-icons/CheckMiniIcon';
import { NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger } from '@radix-ui/react-navigation-menu';
import stepStyles from './Step.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

const Step = (props) => {
    const { id, className = "", href, inverse = false, showLabel = true, stepAmount, stepLabel, stepLocation, stepNumber, stepState, "aria-label": ariaLabel, onClick } = props, rest = __rest(props, ["id", "className", "href", "inverse", "showLabel", "stepAmount", "stepLabel", "stepLocation", "stepNumber", "stepState", "aria-label", "onClick"]);
    const [labelBound, setLabelBound] = useState({ horizontalOffset: 0 });
    const labelRef = useRef(null);
    const generateId = useId();
    const styles = stepStyles({ inverse, stepState, stepLocation });
    // Check State
    const shouldRenderCompleteIcon = stepState === "complete";
    const isInteractive = stepState === "complete" || stepState === "visited";
    const isCurrentStep = stepState === "active";
    // Since setting focus ring based on label text length dynamically
    useEffect(() => {
        const calculateLabelBounds = () => {
            var _a;
            if (labelRef.current && showLabel) {
                const rect = labelRef.current.getBoundingClientRect();
                const parentRect = (_a = labelRef.current.parentElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                if (parentRect) {
                    setLabelBound({
                        horizontalOffset: rect.left - parentRect.left,
                    });
                }
            }
        };
        // Calculate initial bounds
        calculateLabelBounds();
        // Add resize listener to recalculate on window resize
        const handleResize = () => {
            calculateLabelBounds();
        };
        window.addEventListener("resize", handleResize);
        // Cleanup event listener
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [showLabel, stepLabel]);
    // Handle Click
    const handleClick = (e) => {
        if (!isInteractive || !onClick)
            return;
        if (!href) {
            e.preventDefault();
            onClick(stepNumber, e);
        }
    };
    // Handle Props
    const commonProps = {
        "aria-label": ariaLabel || `Step ${stepNumber} of ${stepAmount}: ${stepLabel}`,
        "aria-current": isCurrentStep ? "step" : undefined,
        role: href ? "link" : undefined,
    };
    const interactiveProps = Object.assign(Object.assign({}, commonProps), { href: href ? href : undefined, disabled: false, tabIndex: 0, onClick: handleClick });
    const visualProps = Object.assign(Object.assign({}, commonProps), { href: undefined, disabled: !href ? true : undefined, tabIndex: -1 });
    const responsiveOverlayFocusOffset = labelBound.horizontalOffset !== 0
        ? {
            left: labelBound.horizontalOffset,
            right: labelBound.horizontalOffset,
        }
        : undefined;
    const InteractiveOverLay = href !== undefined ? NavigationMenuLink : NavigationMenuTrigger;
    const renderLabel = (showLabel) => {
        if (showLabel) {
            return (jsx("div", { className: styles.labelContainer(), "aria-hidden": "true", children: jsx("div", { className: styles.label(), children: jsx("span", { className: "iris:inline", ref: labelRef, children: stepLabel }) }) }));
        }
    };
    return (jsx(NavigationMenuItem, { id: id || generateId, className: styles.listContainer(), children: jsxs("div", { className: styles.stepContainer(), children: [isInteractive && (jsx(InteractiveOverLay, Object.assign({}, interactiveProps, { className: styles.interactiveOverlay(), "aria-controls": undefined, "data-state": undefined, "aria-expanded": undefined, "data-disabled": undefined, style: responsiveOverlayFocusOffset }, rest))), jsxs("button", Object.assign({ className: cn(styles.circleLineContainer(), className) }, visualProps, { "aria-hidden": isInteractive, children: [jsx("span", { className: styles.leftTrack() }), jsxs("div", { className: styles.circleNumber(), "aria-hidden": !isInteractive, children: [shouldRenderCompleteIcon && (jsx(CheckMiniIcon, { className: styles.checkMiniIcon() })), !shouldRenderCompleteIcon && (jsx("span", { className: styles.number(), children: stepNumber }))] }), jsx("span", { className: styles.rightTrack() })] })), renderLabel(showLabel)] }) }, stepNumber));
};
Step.displayName = "Step";

export { Step };
